import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Button, Space, Spin, Select, Checkbox, Tooltip, message, Modal, Dropdown } from 'antd';
import {
  ArrowLeftOutlined, ClockCircleOutlined, DownloadOutlined, CopyOutlined,
  LoadingOutlined, GlobalOutlined, DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './MainContent.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createGeminiSummaryJob, endGeminiMerge, getFileStatus, getGeminiFile, getLogDetail, getSummary, getHistory } from '@/api/subtitles';
import RealTimeContent from './RealTimeContent';
import { GeminiGenerateType } from './GeminiSummaryContent';
import GeminiTranscriptionContent from './GeminiTranscriptionContent';
import PostTranslateContent from './PostTranslateContent';
import MeetStatus, { IMeetStatus } from './MeetStatus';
import { trackEvent, TrackingEvent, TabName } from '@/utils/tracking';
import { reportCustomLog } from '@/utils/logReporter';
import { stripMarkdownDividers } from '@/utils/common';
import { useAuthContext } from '@/hooks/auth';

// Tab key 联合类型
type TabKey = 'live' | 'livetr' | 'voc' | 'sum' | 'post' | 'trans';
// 左组(会议过程内容,任何状态可用)/ 右组(会后 AI 生成,会中置灰)
const LEFT_TABS: TabKey[] = ['live', 'livetr'];
const RIGHT_TABS: TabKey[] = ['voc', 'sum', 'post', 'trans'];
const ALL_TABS: TabKey[] = [...LEFT_TABS, RIGHT_TABS[0], RIGHT_TABS[1], RIGHT_TABS[2], RIGHT_TABS[3]];

// GA4 tab_name 埋点映射
const TAB_TRACK: Record<TabKey, TabName> = {
  live: TabName.LIVE_TRANSCRIPT,
  livetr: TabName.LIVE_TRANSLATION,
  voc: TabName.VOC,
  sum: TabName.SUMMARY,
  post: TabName.FINAL_TRANSCRIPT,
  trans: TabName.FINAL_TRANSLATION,
};

// 总结模板(VOC 用 INSIGHTS,单列在 VOC Tab)
const SUMMARY_TEMPLATES = ['STANDARD', 'DECISION_MAKING', 'BRAINSTORMING_IDEAS', 'CUSTOMER_EXTERNAL'] as const;
// 模板 key → 现有 i18n 文案后缀(复用 ai.meetingType.*)
const SUM_I18N: Record<string, string> = {
  STANDARD: 'common',
  DECISION_MAKING: 'decision',
  BRAINSTORMING_IDEAS: 'brainstorming',
  CUSTOMER_EXTERNAL: 'customer',
};

// 输出/翻译语言选项(后端 SubtitleLangEnum 码)
const OUTPUT_LANGS = [
  { code: 'zh_hans', name: '简体中文' },
  { code: 'zh_hant', name: '繁體中文' },
  { code: 'en', name: 'English' },
  { code: 'ja', name: '日本語' },
];

// 界面语言(i18n)切换项:code 对应 i18n 语言码
const UI_LANGS = [
  { code: 'zh-CN', name: '简体中文' },
  { code: 'zh-TW', name: '繁體中文' },
  { code: 'en', name: 'English' },
  { code: 'ja', name: '日本語' },
];

// 进行中(会议未结束)的 merge 状态:显示紫色提示条 +「结束会议」
const IN_PROGRESS_STATUSES: IMeetStatus[] = ['INIT', 'WAIT_DO', 'MERGING', 'RETRY'];

const uiLangToBackend = (lang: string): string => {
  const map: Record<string, string> = {
    'zh-CN': 'zh_hans', 'zh-TW': 'zh_hant', en: 'en', 'en-US': 'en', ja: 'ja', 'ja-JP': 'ja',
  };
  return map[lang] || 'en';
};

const MainContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { subtitle_segment_id: subtitleSegmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { Header, Content } = Layout;
  const { state: { user } } = useAuthContext();

  const [roomId, setRoomId] = useState('');
  const [meetStatus, setMeetStatus] = useState<IMeetStatus>('UNKNOWN');
  const [isFinish, setIsFinish] = useState(false);
  const [title, setTitle] = useState('');
  const [gmtCreate, setGmtCreate] = useState('');
  const [historyData, setHistoryData] = useState<API.SubtitleItem[]>([]);
  const [fileMergeId, setFileMergeId] = useState('');

  // 当前 Tab(URL ?tab= 持久化);默认实时转录
  const urlTab = searchParams.get('tab') as TabKey | null;
  const [activeTab, setActiveTab] = useState<TabKey>(urlTab && ALL_TABS.includes(urlTab) ? urlTab : 'live');

  // 输出/翻译语言(总结/VOC/精校翻译),默认跟界面语言;对照原文开关(两个翻译 Tab 共用)
  const [outputLang, setOutputLang] = useState(uiLangToBackend(i18n.language));
  const [showSource, setShowSource] = useState(false);

  // 实时翻译:可选语言来自会中广播过的译文语言(subtitle_translation_list 里出现过的 lang)
  const realtimeLangs = useMemo(() => {
    const set = new Set<string>();
    historyData.forEach((item) => item.subtitle_translation_list?.forEach((tr) => tr.lang && set.add(tr.lang)));
    return Array.from(set);
  }, [historyData]);
  const [liveLang, setLiveLang] = useState('');
  useEffect(() => {
    if (!liveLang && realtimeLangs.length) setLiveLang(realtimeLangs[0]);
  }, [realtimeLangs, liveLang]);

  // 总结:各模板生成结果缓存 + 当前模板 + 加载态
  const [sumTemplate, setSumTemplate] = useState<string | null>(null);
  const [sumCache, setSumCache] = useState<Record<string, string>>({});
  const [sumLoading, setSumLoading] = useState(false);
  // VOC(INSIGHTS)
  const [vocContent, setVocContent] = useState<string | null>(null);
  const [vocLoading, setVocLoading] = useState(false);
  const [vocError, setVocError] = useState(false);
  // 精校转录/翻译按需触发标记(idempotent 兜底)
  const triggeredRef = useRef<Set<string>>(new Set());
  // 每个视图查看只上报一次
  const reportedViewsRef = useRef<Set<string>>(new Set());
  // Tab Bar 溢出降级:量宽度用
  const barRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const meetingDuration = useMemo(() => {
    if (!historyData.length) return '';
    const first = dayjs(historyData[0].gmt_generate);
    const last = dayjs(historyData[historyData.length - 1].gmt_generate);
    if (!first.isValid() || !last.isValid()) return '';
    const total = Math.abs(last.diff(first, 'second'));
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  }, [historyData]);

  const init = useCallback(async () => {
    if (!subtitleSegmentId) return;
    try {
      const res = await getLogDetail(subtitleSegmentId);
      setTitle(res.data.data.title);
      setGmtCreate(res.data.data.gmt_create);
      setHistoryData(res.data.data.subtitle_list);
      setRoomId(res.data.data.room_id);
    } catch (err: any) {
      setMeetStatus(err?.response?.status === 404 ? 'NO_ACCESS' : 'ERROR');
    }
  }, [subtitleSegmentId]);

  const getContentStatus = async (fileId: string): Promise<IMeetStatus> => {
    try {
      const { data: { data: { status } } } = await getFileStatus({ file_merge_id: fileId });
      return status as IMeetStatus;
    } catch (err) {
      return 'ERROR';
    }
  };

  const getHistoryData = useCallback(async (newRoomId: string) => {
    try {
      const { data: { data: { file_merge_id } } } = await getGeminiFile({ roomId: newRoomId });
      setFileMergeId(file_merge_id);
      const status = await getContentStatus(file_merge_id);
      setMeetStatus(status);
      setIsFinish(status === 'FINISH');
    } catch (err) {
      setMeetStatus('ERROR');
    }
  }, []);

  useEffect(() => { init(); }, [init]);
  useEffect(() => { if (roomId) getHistoryData(roomId); }, [roomId, getHistoryData]);

  // 会中不允许停留在右组;进行中默认回到实时转录
  useEffect(() => {
    if (meetStatus !== 'UNKNOWN' && meetStatus !== 'FINISH' && RIGHT_TABS.includes(activeTab)) {
      setActiveTab('live');
    }
  }, [meetStatus, activeTab]);

  // 日志平台查看上报:仅 FINISH,每视图去重一次
  const reportView = useCallback((event: string, extra?: Record<string, unknown>) => {
    if (meetStatus !== 'FINISH') return;
    if (reportedViewsRef.current.has(event)) return;
    reportedViewsRef.current.add(event);
    reportCustomLog(event, user?.uid, {
      room_id: roomId, subtitle_segment_id: subtitleSegmentId, file_merge_id: fileMergeId, ...extra,
    }, 'Manual');
  }, [meetStatus, user?.uid, roomId, subtitleSegmentId, fileMergeId]);

  const handleBackClick = useCallback(() => {
    trackEvent(TrackingEvent.BACK_CLICK, {});
    navigate('/record');
  }, [navigate]);

  const handleEndMeeting = useCallback(async () => {
    try {
      const endMergeResponse = await endGeminiMerge(roomId);
      if (endMergeResponse?.data?.status !== 200) return;
      setMeetStatus('WAIT_DO');
    } catch (err) {
      console.error('Failed to end meeting:', err);
    }
  }, [roomId]);

  // 结束会议二次确认(与原 MeetStatus 一致)
  const confirmEndMeeting = useCallback(() => {
    Modal.confirm({
      title: t('meeting.confirmEndMeeting'),
      icon: null,
      content: t('meeting.endMeetingWarning'),
      okText: t('meeting.endMeeting'),
      cancelText: t('meeting.cancel'),
      onOk() { handleEndMeeting(); },
    });
  }, [t, handleEndMeeting]);

  // 生成总结(指定模板)。force=true 时不看当前缓存强制生成(切语言场景:
  // setSumCache({}) 是异步的,不 force 会读到旧缓存而跳过,导致新语言永不生成)
  const generateSummary = useCallback(async (template: string, force = false) => {
    if (!subtitleSegmentId || sumLoading) return;
    setSumTemplate(template);
    if (!force && sumCache[template]) return; // 已生成,直接展示
    setSumLoading(true);
    try {
      const res = await getSummary({
        user_subtitle_segment_id: subtitleSegmentId,
        target_lang: outputLang,
        summary_prompt_type_enum: template,
      });
      const text = res.data.data.summary_details?.summary_text || '';
      setSumCache((prev) => ({ ...prev, [template]: text }));
      reportView('summary_view', { summary_type: template });
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      message.error(msg?.includes('Insufficient') ? t('ai.insufficientContent') : t('ai.fetchFailed'));
    } finally {
      setSumLoading(false);
    }
  }, [subtitleSegmentId, outputLang, sumCache, sumLoading, reportView, t]);

  // 生成 VOC(INSIGHTS)。force 同上,切语言时强制重生成,不被旧内容挡
  const generateVoc = useCallback(async (force = false) => {
    if (!subtitleSegmentId || vocLoading) return;
    if (!force && vocContent) return;
    setVocError(false);
    setVocLoading(true);
    try {
      const res = await getSummary({
        user_subtitle_segment_id: subtitleSegmentId,
        target_lang: outputLang,
        summary_prompt_type_enum: 'INSIGHTS',
      });
      setVocContent(res.data.data.summary_details?.summary_text || '');
      reportView('voc_view');
    } catch (err: any) {
      setVocError(true);
      const msg = err?.response?.data?.message;
      message.error(msg?.includes('Insufficient') ? t('ai.insufficientContent') : t('ai.fetchFailed'));
    } finally {
      setVocLoading(false);
    }
  }, [subtitleSegmentId, outputLang, vocContent, vocLoading, reportView, t]);

  // 精校转录/翻译按需触发建任务(GeminiTranscriptionContent 负责轮询展示)
  const triggerGenerate = useCallback((promptTypes: string[], key: string) => {
    if (!fileMergeId || triggeredRef.current.has(key)) return;
    triggeredRef.current.add(key);
    createGeminiSummaryJob(fileMergeId, outputLang, promptTypes).catch(() => {
      triggeredRef.current.delete(key); // 失败允许重试
    });
  }, [fileMergeId, outputLang]);

  // 切 Tab:更新状态 + URL + 埋点 + 按需触发
  const goTab = useCallback((key: TabKey) => {
    setActiveTab(key);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('tab', key); return p; }, { replace: true });
    trackEvent(TrackingEvent.TAB_CLICK, { tab_name: TAB_TRACK[key] });
    if (meetStatus !== 'FINISH') return;
    if (key === 'voc') generateVoc();
    if (key === 'post') { triggerGenerate(['TRANSCRIPTION_PROMPT'], 'post'); reportView('transcription_view'); }
    // 精校翻译走文本翻译(PostTranslateContent 自行请求),此处仅确保翻译源"精校转录"已在生成
    if (key === 'trans') { triggerGenerate(['TRANSCRIPTION_PROMPT'], 'post'); reportView('translation_view'); }
  }, [meetStatus, generateVoc, triggerGenerate, reportView, setSearchParams]);

  // 进页面/切语言时加载已生成的总结/VOC(按 prompt_type 填入对应 Tab、点亮绿点),
  // 不重复生成、不重复扣费(结果持久化)。老数据 target_lang 为空则忽略
  const loadHistory = useCallback(async (lang: string) => {
    const loaded = { cache: {} as Record<string, string>, voc: null as string | null };
    if (!subtitleSegmentId) return loaded;
    try {
      const res = await getHistory({ user_subtitle_segment_id: subtitleSegmentId });
      const list = res.data.data.summary_details_list || [];
      let latestTpl: string | null = null;
      list.forEach((d) => {
        if (!d.prompt_type || (d.target_lang && d.target_lang !== lang)) return;
        if (d.prompt_type === 'INSIGHTS') { loaded.voc = d.summary_text; return; }
        if ((SUMMARY_TEMPLATES as readonly string[]).includes(d.prompt_type)) {
          loaded.cache[d.prompt_type] = d.summary_text;
          latestTpl = d.prompt_type;
        }
      });
      setSumCache(loaded.cache);
      if (latestTpl) setSumTemplate(latestTpl);
      setVocContent(loaded.voc);
    } catch (e) {
      // 加载失败不影响,点击时再按需生成
    }
    return loaded;
  }, [subtitleSegmentId]);

  // 输出语言切换:清缓存 → 重载该语言已生成结果 → 当前 Tab 若无则重新生成(跳过首次挂载)
  const outputLangInit = useRef(true);
  useEffect(() => {
    if (outputLangInit.current) { outputLangInit.current = false; return; }
    if (meetStatus !== 'FINISH') return;
    setSumCache({});
    setVocContent(null);
    setVocError(false);
    // 先按新语言重载已生成结果;该语言没有的才 force 生成(有缓存不重复请求)
    (async () => {
      const loaded = await loadHistory(outputLang);
      if (activeTab === 'sum' && sumTemplate && !loaded.cache[sumTemplate]) generateSummary(sumTemplate, true);
      if (activeTab === 'voc' && !loaded.voc) generateVoc(true);
      // 精校翻译:PostTranslateContent 以 outputLang 为 key 重挂载并自动重译,无需在此触发
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputLang]);

  // FINISH 后:先加载已生成结果(持久化),再按 URL 指向的 Tab 触发未生成的(刷新还原场景)
  useEffect(() => {
    if (meetStatus !== 'FINISH') return;
    (async () => {
      const loaded = await loadHistory(outputLang);
      if (activeTab === 'voc' && !loaded.voc) generateVoc();
      if (activeTab === 'post') {
        triggerGenerate(['TRANSCRIPTION_PROMPT'], 'post');
        reportView('transcription_view');
      }
      if (activeTab === 'trans') {
        triggerGenerate(['TRANSCRIPTION_PROMPT'], 'post');
        reportView('translation_view');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetStatus]);

  // Tab Bar 两级溢出降级:① 放不下先把右侧工具收成纯图标(compact) ② 仍放不下则 Tab 区横向滚动 + 左右渐隐,选中项自动滚入
  useEffect(() => {
    const bar = barRef.current;
    const tabs = tabsRef.current;
    if (!bar || !tabs) return undefined;
    const updateFade = () => {
      tabs.classList.toggle('fade-r', tabs.scrollLeft + tabs.clientWidth < tabs.scrollWidth - 2);
      tabs.classList.toggle('fade-l', tabs.scrollLeft > 2);
    };
    const measure = () => {
      bar.classList.remove('compact'); // 先按完整工具栏量一次
      if (tabs.scrollWidth > tabs.clientWidth + 1) bar.classList.add('compact');
      updateFade();
    };
    measure();
    tabs.querySelector('.analyse-tab.on')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    tabs.addEventListener('scroll', updateFade, { passive: true });
    return () => {
      ro.disconnect();
      tabs.removeEventListener('scroll', updateFade);
    };
  }, [activeTab, meetStatus]);

  const rightDisabled = meetStatus !== 'FINISH';
  const tabLabel = (key: TabKey) => t(`analyseTab.${key}`);
  const tabHint = (key: TabKey) => t(`analyseTab.hint.${key}`);

  // 本地 TXT 下载
  const downloadTxt = (text: string) => {
    if (!text.trim()) { message.info(t('ai.noContent')); return; }
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'transcript'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (activeTab === 'live') {
      downloadTxt(historyData.map((i) => i.text).filter(Boolean).join('\n'));
      return;
    }
    if (activeTab === 'livetr') {
      const lines = historyData.map((i) => {
        const tr = liveLang ? i.subtitle_translation_list?.find((x) => x.lang === liveLang) : undefined;
        const translated = (tr || i.subtitle_translation_list?.[0])?.text || i.text;
        // 勾选对照原文时,下载一并带上原文(与页面显示一致)
        if (showSource && i.text && translated !== i.text) return `${translated}\n${i.text}`;
        return translated;
      });
      downloadTxt(lines.filter(Boolean).join('\n\n'));
      return;
    }
    // 精校转录/翻译:内容在 GeminiTranscriptionContent 内,抓 DOM 逐行拼(说话人 + 文本 [+ 对照原文])
    const rows = Array.from(document.querySelectorAll('.analyse-pane .transcript-row'));
    const text = rows.map((row) => {
      const spk = row.querySelector('.speaker-column span')?.textContent?.trim() || '';
      const content = row.querySelector('.content-column .source-text')?.textContent?.trim() || '';
      // .translate-text 仅在勾选对照原文时渲染,存在即带上原文
      const orig = row.querySelector('.content-column .translate-text')?.textContent?.trim() || '';
      return [spk, content, orig].filter(Boolean).join('\n');
    }).filter(Boolean).join('\n\n');
    downloadTxt(text);
  };

  const handleCopy = () => {
    let text = '';
    if (activeTab === 'voc') text = vocContent || '';
    else if (sumTemplate) text = sumCache[sumTemplate] || '';
    if (!text.trim()) { message.info(t('ai.noContent')); return; }
    navigator.clipboard?.writeText(text).then(
      () => message.success(t('ai.copied') || 'Copied'),
      () => message.error(t('ai.fetchFailed')),
    );
  };

  const renderTab = (key: TabKey) => {
    const disabled = RIGHT_TABS.includes(key) && rightDisabled;
    const done = (key === 'sum' && !!sumTemplate && !!sumCache[sumTemplate])
      || (key === 'voc' && !!vocContent);
    // 置灰 Tab 不用 disabled 属性:点击时给 toast(移动端无 hover,tooltip 不可见)
    const onClick = () => (disabled ? message.info(t('analyseTab.locked')) : goTab(key));
    return (
      <Tooltip key={key} title={disabled ? t('analyseTab.locked') : tabHint(key)}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === key}
          className={`analyse-tab ${activeTab === key ? 'on' : ''} ${disabled ? 'disabled' : ''}`}
          aria-disabled={disabled}
          onClick={onClick}
        >
          {tabLabel(key)}
          {done && <i className="analyse-tab-done" />}
        </button>
      </Tooltip>
    );
  };

  // 右侧工具区随 Tab 变化
  const renderTools = () => {
    const dl = (
      <Button size="small" icon={<DownloadOutlined />} className="analyse-tool-btn" onClick={handleDownload}>
        <span className="analyse-tool-lbl">{t('analyseTab.tool.dl')}</span>
      </Button>
    );
    const cp = (
      <Button size="small" icon={<CopyOutlined />} className="analyse-tool-btn" onClick={handleCopy}>
        <span className="analyse-tool-lbl">{t('analyseTab.tool.cp')}</span>
      </Button>
    );
    const srcToggle = (
      <Checkbox checked={showSource} onChange={(e) => setShowSource(e.target.checked)} title={t('analyseTab.tool.src')}>
        <span className="analyse-tool-lbl">{t('analyseTab.tool.src')}</span>
      </Checkbox>
    );
    // 输出语言:总结/VOC/精校翻译用后端语言集(切换会重新生成)
    const outputSel = (
      <Select
        size="small"
        value={outputLang}
        style={{ width: 130 }}
        onChange={setOutputLang}
        options={OUTPUT_LANGS.map((l) => ({ value: l.code, label: l.name }))}
      />
    );
    switch (activeTab) {
      case 'live': return dl;
      // 实时翻译:只回放会中广播过的译文,不提供语言切换(任意语言翻译在精校翻译)
      case 'livetr': return <Space>{srcToggle}{dl}</Space>;
      case 'post': return dl;
      case 'trans': return <Space>{srcToggle}{outputSel}{dl}</Space>;
      case 'sum': return sumTemplate && sumCache[sumTemplate] ? <Space>{outputSel}{cp}</Space> : null;
      case 'voc': return <Space>{outputSel}{cp}</Space>;
      default: return null;
    }
  };

  // 生成中富文案(对齐 mock):AI 正在分析·正在从会议内容中<动作>…
  const genLoading = (action: string) => (
    <div className="gemini-content">
      <div className="loading-container">
        <div className="loading-text">
          <LoadingOutlined />
          {`${t('ai.generatingTitle')}·${t('ai.generatingDescPrefix')}${action}…`}
        </div>
      </div>
    </div>
  );

  // 加载已有结果 / 语言切换空档期:显示"加载中"而非"生成中",避免误会成重新生成扣费
  const fetchingBlock = (
    <div className="gemini-content">
      <div className="loading-container">
        <div className="loading-text"><LoadingOutlined />{t('ai.loading')}</div>
      </div>
    </div>
  );

  const renderSumPane = () => {
    if (sumLoading) return genLoading(t('ai.loadingAction.common'));
    if (sumTemplate && sumCache[sumTemplate]) {
      return (
        <>
          <div className="analyse-chips">
            {SUMMARY_TEMPLATES.map((k) => (
              <button type="button" key={k} className={`analyse-chip ${k === sumTemplate ? 'on' : ''}`} onClick={() => generateSummary(k)}>
                {t(`ai.meetingType.${SUM_I18N[k]}`)}
              </button>
            ))}
          </div>
          <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]}>
            {stripMarkdownDividers(sumCache[sumTemplate])}
          </ReactMarkdown>
        </>
      );
    }
    return (
      <>
        <h2 className="analyse-sum-h">{t('analyseTab.sumPick')}</h2>
        <div className="analyse-tpl-grid">
          {SUMMARY_TEMPLATES.map((k) => (
            <button type="button" key={k} className="analyse-tpl" onClick={() => generateSummary(k)}>
              <b>{t(`ai.meetingType.${SUM_I18N[k]}`)}</b>
              <span>{t(`ai.meetingType.${SUM_I18N[k]}Desc`)}</span>
            </button>
          ))}
        </div>
      </>
    );
  };

  const renderVocPane = () => {
    if (vocContent && !vocLoading) {
      return (
        <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]}>
          {stripMarkdownDividers(vocContent)}
        </ReactMarkdown>
      );
    }
    // 仅生成失败时给重试入口;切换语言的空档期(未失败、即将加载)显示转圈,避免闪现"重试"
    if (vocError && !vocLoading) {
      return (
        <div className="gemini-content">
          <div className="loading-container">
            <Button type="link" onClick={() => generateVoc(true)}>{t('ai.retry')}</Button>
          </div>
        </div>
      );
    }
    // vocLoading=真正在调 create 生成 → "生成中";否则是加载已有结果的空档期 → "加载中"
    return vocLoading ? genLoading(t('ai.loadingAction.insights')) : fetchingBlock;
  };

  const renderPane = () => {
    switch (activeTab) {
      case 'live':
        return <RealTimeContent roomId={roomId} historyData={historyData} mode="original" />;
      case 'livetr':
        return (
          <RealTimeContent
            roomId={roomId}
            historyData={historyData}
            mode="translation"
            showSource={showSource}
            targetLang={liveLang}
          />
        );
      case 'post':
        return (
          <GeminiTranscriptionContent
            isFinish={isFinish}
            meetStatus={meetStatus}
            fileMergeId={fileMergeId}
            generateType={GeminiGenerateType.TRANSCRIPTION}
            noSpeech={historyData.length === 0}
          />
        );
      case 'trans':
        return (
          <PostTranslateContent
            key={`trans-${outputLang}`}
            isFinish={isFinish}
            meetStatus={meetStatus}
            subtitleSegmentId={subtitleSegmentId || ''}
            fileMergeId={fileMergeId}
            targetLang={outputLang}
            showSource={showSource}
            noSpeech={historyData.length === 0}
          />
        );
      case 'sum':
        return <div className="analyse-sum">{renderSumPane()}</div>;
      case 'voc':
        return <div className="analyse-voc">{renderVocPane()}</div>;
      default: return null;
    }
  };

  const renderMain = () => {
    if (meetStatus === 'UNKNOWN') {
      return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
    }
    if (meetStatus === 'NO_ACCESS') {
      return (
        <Content style={{ padding: '12px', maxWidth: 820 }}>
          <MeetStatus meetStatus={meetStatus} contentType="transcription" />
        </Content>
      );
    }
    return (
      <>
        {/* 顶部 Tab Bar */}
        <div className="analyse-bar" ref={barRef}>
          <div className="analyse-tabs" ref={tabsRef} role="tablist">
            {renderTab('live')}
            {renderTab('livetr')}
            <span className="analyse-tab-sep" />
            {RIGHT_TABS.map((k) => renderTab(k))}
          </div>
          <div className="analyse-tools">{renderTools()}</div>
        </div>
        <Content className="analyse-body">
          {/* 进行中:紫色提示条 +「结束会议」(参照原型);其余非 FINISH 状态用 MeetStatus 展示错误/过期等 */}
          {IN_PROGRESS_STATUSES.includes(meetStatus) ? (
            <div className="analyse-strip">
              <span>
                <b>{t('meeting.meetingInProgress')}</b>
                {t('analyseTab.strip.unlock')}
              </span>
              <span style={{ flex: 1 }} />
              <button type="button" className="analyse-strip-end" onClick={confirmEndMeeting}>
                {t('meeting.endMeeting')}
              </button>
            </div>
          ) : rightDisabled && (
            <MeetStatus meetStatus={meetStatus} handleEndMeeting={handleEndMeeting} contentType="summary" compact />
          )}
          <div className="analyse-hint">{tabHint(activeTab)}</div>
          <div className="analyse-pane">{renderPane()}</div>
        </Content>
      </>
    );
  };

  return (
    <Layout className="analyse-layout" style={{ background: '#fff' }}>
      <Header className="analyse-header">
        {/* 顺序参照原型:标题 → 会议进行中 → 时长 → 日期 */}
        <div className="analyse-head-meta">
          <Button
            className="analyse-page-btn"
            aria-label={t('common.back') || 'Back'}
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={handleBackClick}
          />
          <h2 className="analyse-page-title" title={title}>{title}</h2>
          {/* 只有真正处理中(INIT/WAIT_DO/MERGING/RETRY)才显示"会议进行中";
              SKIPPED(不足1分钟)/FAILED/EXPIRE 等是终态,不是进行中 */}
          {IN_PROGRESS_STATUSES.includes(meetStatus) && (
            <span className="analyse-live-dot">{t('meeting.meetingInProgress')}</span>
          )}
          {meetingDuration && (
            <span title={t('meeting.duration')} className="analyse-duration">
              <ClockCircleOutlined style={{ marginRight: 4 }} />{meetingDuration}
            </span>
          )}
          {gmtCreate && (
            <span className="analyse-date">{dayjs(gmtCreate).format('YYYY-MM-DD HH:mm')}</span>
          )}
        </div>
        <div className="analyse-head-right">
          <Dropdown
            trigger={['click']}
            menu={{
              selectable: true,
              selectedKeys: [i18n.language],
              items: UI_LANGS.map((l) => ({ key: l.code, label: l.name })),
              onClick: ({ key }) => i18n.changeLanguage(key),
            }}
          >
            <button type="button" className="analyse-ui-lang" aria-haspopup="menu" aria-label={t('analyseTab.uiLang')} title={t('analyseTab.uiLang')}>
              <GlobalOutlined />
              <span className="analyse-ui-lang-name">
                {(UI_LANGS.find((l) => l.code === i18n.language) || UI_LANGS[0]).name}
              </span>
              <DownOutlined className="analyse-ui-lang-caret" />
            </button>
          </Dropdown>
        </div>
      </Header>
      {renderMain()}
    </Layout>
  );
};

export default MainContent;
